import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { dismissInvoiceProcess } from './dismissInvoiceProcess';
import { type DismissInvoiceProcessResponse } from './types';

/**
 * A custom hook to dismiss a reprocessing invoice process with a given process ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @param options - Optional configuration for the mutation.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/dismiss_invoice_process_invoice_process__process_id__dismiss_post
 */
export const useMutationDismissInvoiceProcess = (
  options?: UseMutationOptions<DismissInvoiceProcessResponse, unknown, UUID>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (processId: UUID) => dismissInvoiceProcess(processId),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getAll({}),
      });
    },
    ...options,
  });
};
